import { NAV_LINKS, SITE_URL } from "@/data/services";
import { useSiteContent } from "@/hooks/useSiteContent";
import { Facebook, Twitter, Youtube, Instagram, ChevronRight } from "lucide-react";

const FOOTER_LOGO =
  "https://wewonderevents.com/wp-content/uploads/2026/08/WhatsApp-Image-2026-08-06-at-6.37.38-PM.jpeg";

const SOCIALS = [
  { Icon: Facebook, bg: "bg-[#1877F2]" },
  { Icon: Twitter, bg: "bg-black" },
  { Icon: Youtube, bg: "bg-[#FF0000]" },
  { Icon: Instagram, bg: "bg-[#3A3A3A]" },
];

const FooterColumn = ({ title, children, testid }) => (
  <div data-testid={testid}>
    <h4 className="text-lg font-bold text-white">{title}</h4>
    <div className="mt-5">{children}</div>
  </div>
);

const Footer = () => {
  const s = useSiteContent();
  const footerServices = s.footer_services.split(",").map((v) => v.trim()).filter(Boolean);

  return (
    <footer data-testid="site-footer" className="bg-[#1A1A1A] text-white">
      <div className="mx-auto grid max-w-[1340px] gap-12 px-6 py-16 sm:grid-cols-2 lg:grid-cols-4 lg:gap-16 lg:px-10 lg:py-20">
        <div>
          <img
            src={FOOTER_LOGO}
            alt="Wonder Events & Entertainment Pvt. Ltd."
            className="w-52 rounded-sm"
            loading="lazy"
          />
          <p className="mt-5 text-lg font-bold leading-snug text-white">
            Wonder Events &<br />Entertainment Pvt. Ltd.
          </p>
          <p className="mt-4 text-sm leading-[1.9] text-[#B5B5B5]">{s.footer_description}</p>
          <div className="mt-6 flex gap-3">
            {SOCIALS.map(({ Icon, bg }, i) => (
              <a
                key={i}
                data-testid={`footer-social-${i}`}
                href={SITE_URL}
                aria-label="Social link"
                className={`flex h-9 w-9 items-center justify-center rounded-full ${bg} text-white transition-transform duration-200 hover:-translate-y-0.5`}
              >
                <Icon size={15} />
              </a>
            ))}
          </div>
        </div>

        <FooterColumn title="Quick Links" testid="footer-quick-links">
          <ul className="space-y-4">
            {NAV_LINKS.map((link) => (
              <li key={link.label}>
                <a
                  data-testid={`footer-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  href={link.href}
                  className="group flex items-center gap-2.5 text-sm text-[#B5B5B5] transition-colors duration-200 hover:text-white"
                >
                  <ChevronRight size={14} strokeWidth={3} className="text-[#C62828]" />
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </FooterColumn>

        <FooterColumn title="Our Services" testid="footer-services">
          <ul className="space-y-4">
            {footerServices.map((svc) => (
              <li key={svc}>
                <a
                  data-testid={`footer-service-${svc.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  href="/services"
                  className="group flex items-center gap-2.5 text-sm text-[#B5B5B5] transition-colors duration-200 hover:text-white"
                >
                  <ChevronRight size={14} strokeWidth={3} className="text-[#C62828]" />
                  {svc}
                </a>
              </li>
            ))}
          </ul>
        </FooterColumn>

        <FooterColumn title="Contact Us" testid="footer-contact">
          <div className="space-y-5">
            <div>
              <p className="text-base font-bold text-white">Address</p>
              <p className="mt-1.5 text-sm leading-[1.9] text-[#B5B5B5]">{s.address}</p>
            </div>
            <div>
              <p className="text-base font-bold text-white">Phone Number</p>
              <p className="mt-1.5 text-sm leading-[1.9] text-[#B5B5B5]">
                {s.phone_1},
                <br />
                {s.phone_2}.
              </p>
            </div>
            <div>
              <p className="text-base font-bold text-white">Email</p>
              <p className="mt-1.5 text-sm text-[#B5B5B5]">{s.email}</p>
            </div>
            <div>
              <p className="text-base font-bold text-white">Working Hours</p>
              <p className="mt-1.5 whitespace-pre-line text-sm leading-[1.9] text-[#B5B5B5]">{s.working_hours}</p>
            </div>
          </div>
        </FooterColumn>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-[1340px] flex-col items-center justify-between gap-2 px-6 py-6 text-center sm:flex-row lg:px-10">
          <p data-testid="footer-copyright" className="text-xs text-[#9A9A9A]">
            {s.copyright_text}
          </p>
          <p className="text-sm font-bold text-white">
            The Aroma of <span className="text-[#C62828]">Innovation</span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
